import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { toast } from "sonner";
import { useWorkspaceStore } from "@/hooks/useWorkspace";
import ChevronDownSm from "@/assets/icons/ChevronDownSm.svg";
import SearchSm from "@/assets/icons/SearchSm.svg";
import Checkmark from "@/assets/icons/Checkmark.svg";
import { API_URL } from "@/env";
import { showCustomToast } from "../CustomToast";

interface LinearIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  reproductionSteps?: string;
}

interface LinearTeam {
  id: string;
  key: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
}

interface LinearUser {
  id: string;
  name: string;
  email?: string;
  displayName: string;
  avatarUrl?: string;
  active: boolean;
}

interface LinearIssue {
  id: string;
  identifier: string;
  title: string;
  state?: {
    name: string;
    type: string;
  };
}

interface LinearState {
  id: string;
  name: string;
  type: string;
  color?: string;
  position?: number;
}

interface LinearProject {
  id: string;
  name: string;
  description?: string;
  color?: string;
  state?: string;
}

const CircleLoader = () => (
  <div className="flex items-center justify-center py-4">
    <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
  </div>
);

const LinearIssueModal = ({
  isOpen,
  onClose,
  reportId,
  reproductionSteps = "",
}: LinearIssueModalProps) => {
  const { currentWorkspace } = useWorkspaceStore();
  const [teams, setTeams] = useState<LinearTeam[]>([]);
  const [users, setUsers] = useState<LinearUser[]>([]);
  const [parentIssues, setParentIssues] = useState<LinearIssue[]>([]);
  const [states, setStates] = useState<LinearState[]>([]);
  const [projects, setProjects] = useState<LinearProject[]>([]);
  const [creating, setCreating] = useState(false);

  // Loading states for each dropdown
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingParentIssues, setLoadingParentIssues] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const summaryRef = React.useRef<HTMLTextAreaElement>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [formData, setFormData] = useState({
    teamId: "",
    title: "",
    description: "",
    assigneeId: "",
    parentIssueId: "",
    priority: "",
    stateId: "",
    projectId: "",
  });

  const [dropdownPosition, setDropdownPosition] = useState<{
    [key: string]: "top" | "bottom";
  }>({});

  // Refs for dropdown containers
  const teamDropdownRef = useRef<HTMLDivElement>(null);
  const parentIssueDropdownRef = useRef<HTMLDivElement>(null);
  const priorityDropdownRef = useRef<HTMLDivElement>(null);
  const assigneeDropdownRef = useRef<HTMLDivElement>(null);
  const stateDropdownRef = useRef<HTMLDivElement>(null);
  const projectDropdownRef = useRef<HTMLDivElement>(null);

  // Priority options for Linear (0 = No priority, 1 = Urgent, 2 = High, 3 = Medium, 4 = Low)
  const priorities = [
    { id: "0", name: "No priority" },
    { id: "1", name: "Urgent" },
    { id: "2", name: "High" },
    { id: "3", name: "Medium" },
    { id: "4", name: "Low" },
  ];

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !openDropdown) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isOpen, onClose, openDropdown]);

  // Handle outside clicks and positioning for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const dropdownRefs = {
        team: teamDropdownRef,
        parentIssue: parentIssueDropdownRef,
        priority: priorityDropdownRef,
        assignee: assigneeDropdownRef,
        state: stateDropdownRef,
        project: projectDropdownRef,
      };

      const currentRef = openDropdown
        ? dropdownRefs[openDropdown as keyof typeof dropdownRefs]
        : null;

      if (currentRef?.current && !currentRef.current.contains(target)) {
        setOpenDropdown(null);
        setSearchQuery("");
      }
    };

    const checkDropdownPosition = () => {
      if (!openDropdown) return;

      const dropdownRefs = {
        team: teamDropdownRef,
        parentIssue: parentIssueDropdownRef,
        priority: priorityDropdownRef,
        assignee: assigneeDropdownRef,
        state: stateDropdownRef,
        project: projectDropdownRef,
      };

      const currentRef =
        dropdownRefs[openDropdown as keyof typeof dropdownRefs];
      if (currentRef?.current) {
        const button = currentRef.current.querySelector("button");
        if (button) {
          const rect = button.getBoundingClientRect();
          const dropdownHeight = 172; // max-h-[172px]
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;

          if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            setDropdownPosition((prev) => ({ ...prev, [openDropdown]: "top" }));
          } else {
            setDropdownPosition((prev) => ({
              ...prev,
              [openDropdown]: "bottom",
            }));
          }
        }
      }
    };

    if (openDropdown) {
      checkDropdownPosition();
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [openDropdown]);

  // Auto-resize summary textarea
  const handleTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData({ ...formData, title: e.target.value });

    // Reset height to minHeight to get the correct scrollHeight
    e.target.style.height = "28px";
    // Set height to scrollHeight to fit content (minimum 28px)
    e.target.style.height = `${Math.max(e.target.scrollHeight, 28)}px`;
  };

  // Reset textarea height when modal opens and populate description with reproduction steps
  useEffect(() => {
    if (isOpen && summaryRef.current) {
      summaryRef.current.style.height = "28px";
    }
    if (isOpen && reproductionSteps) {
      setFormData((prev) => ({
        ...prev,
        description: reproductionSteps,
      }));
    }
  }, [isOpen, reproductionSteps]);

  // Fetch teams when modal opens
  useEffect(() => {
    if (isOpen && currentWorkspace) {
      fetchTeams();
    }
  }, [isOpen, currentWorkspace]);

  // Fetch users, issues, states, and projects when team changes
  useEffect(() => {
    if (formData.teamId && currentWorkspace) {
      fetchUsers(formData.teamId);
      fetchParentIssues(formData.teamId);
      fetchStates(formData.teamId);
      fetchProjects(formData.teamId);
    }
  }, [formData.teamId, currentWorkspace]);

  const fetchTeams = async () => {
    setLoadingTeams(true);
    try {
      const response = await fetch(
        `${API_URL}/linear/${currentWorkspace?._id}/teams`
      );

      if (!response.ok) throw new Error("Failed to fetch teams");

      const data = await response.json();
      setTeams(data);
    } catch (error) {
      console.error("Error fetching teams:", error);
      toast.error("Failed to load Linear teams");
    } finally {
      setLoadingTeams(false);
    }
  };

  const fetchUsers = async (teamId: string) => {
    setLoadingUsers(true);
    try {
      const response = await fetch(
        `${API_URL}/linear/${currentWorkspace?._id}/${teamId}/users`
      );

      if (!response.ok) throw new Error("Failed to fetch users");

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load assignable users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchParentIssues = async (teamId: string) => {
    setLoadingParentIssues(true);
    try {
      const response = await fetch(
        `${API_URL}/linear/${currentWorkspace?._id}/${teamId}/issues`
      );

      if (!response.ok) throw new Error("Failed to fetch parent issues");

      const data = await response.json();
      setParentIssues(data);
    } catch (error) {
      console.error("Error fetching parent issues:", error);
      toast.error("Failed to load parent issues");
    } finally {
      setLoadingParentIssues(false);
    }
  };

  const fetchStates = async (teamId: string) => {
    setLoadingStates(true);
    try {
      const response = await fetch(
        `${API_URL}/linear/${currentWorkspace?._id}/${teamId}/states`
      );

      if (!response.ok) throw new Error("Failed to fetch states");

      const data = await response.json();
      setStates(data);
    } catch (error) {
      console.error("Error fetching states:", error);
      toast.error("Failed to load workflow states");
    } finally {
      setLoadingStates(false);
    }
  };

  const fetchProjects = async (teamId: string) => {
    setLoadingProjects(true);
    try {
      const response = await fetch(
        `${API_URL}/linear/${currentWorkspace?._id}/${teamId}/projects`
      );

      if (!response.ok) throw new Error("Failed to fetch projects");

      const data = await response.json();
      setProjects(data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.teamId || !formData.title) {
      showCustomToast("Please fill in all required fields", "exclamation");
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(
        `${API_URL}/linear/create-issue/${currentWorkspace?._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reportId,
            teamId: formData.teamId,
            title: formData.title,
            description: formData.description,
            assigneeId: formData.assigneeId || undefined,
            parentIssueId: formData.parentIssueId || undefined,
            priority: formData.priority
              ? parseInt(formData.priority)
              : undefined,
            stateId: formData.stateId || undefined,
            projectId: formData.projectId || undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error response:", errorData);
        throw new Error("Failed to create issue");
      }

      const data = await response.json();

      // console.log({ data });

      // Copy issue URL to clipboard if available
      if (data?.url) {
        await navigator.clipboard.writeText(data?.url);
        showCustomToast(
          "Linear issue created! Link copied to clipboard",
          "check"
        );
      } else {
        showCustomToast("Linear issue created successfully!", "check");
      }
      onClose();

      // Reset form
      setFormData({
        teamId: "",
        title: "",
        description: "",
        assigneeId: "",
        parentIssueId: "",
        priority: "",
        stateId: "",
        projectId: "",
      });
    } catch (error) {
      console.error("Error creating issue:", error);
      toast.error("Failed to create Linear issue");
    } finally {
      setCreating(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="h-[640px] flex flex-col gap-[32px] w-[480px] p-[20px] bg-gray-200 rounded-[24px] max-w-[520px] mx-4 overflow-visible"
          >
            {/* Form */}
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-[20px] h-full"
            >
              <div className="h-full overflow-y-auto no-scrollbar">
                {/* Title Textarea */}
                <textarea
                  ref={summaryRef}
                  placeholder="Enter issue title..."
                  value={formData.title}
                  onChange={handleTitleChange}
                  className="!outline-none resize-none bg-transparent w-full text-white text-[20px] leading-[28px] font-medium placeholder:text-white/[0.32] overflow-hidden"
                  rows={1}
                />

                {/* Description Textarea */}
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Description"
                  className="!outline-none mt-4 resize-none no-scrollbar h-full bg-transparent w-full text-white text-[14px] leading-[20px] placeholder:text-white/[0.32]"
                />
              </div>

              <div className="flex flex-col gap-[2px]">
                {/* Team Selection - Custom Dropdown */}
                <div className="relative shrink-0" ref={teamDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpenDropdown(openDropdown === "team" ? null : "team");
                      setSearchQuery("");
                    }}
                    className="grid grid-cols-2 w-full h-[32px] items-center text-[14px] leading-[20px] px-3 gap-1.5 rounded-[12px]  hover:bg-white/[0.08] transition-colors"
                  >
                    <p className="text-left">Team</p>
                    <div className="flex items-center gap-2 justify-between">
                      <span className="text-white truncate">
                        {formData.teamId
                          ? teams.find((t) => t.id === formData.teamId)?.name
                          : "Select"}
                      </span>
                      <img src={ChevronDownSm} />
                    </div>
                  </button>

                  <AnimatePresence>
                    {openDropdown === "team" && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`absolute max-h-[172px] w-[50%] right-0 bg-gray-400 rounded-[16px] overflow-hidden z-50 shadow-2xl ${
                          dropdownPosition.team === "top"
                            ? "bottom-full mb-2"
                            : "top-full mt-2"
                        }`}
                      >
                        {/* Search Input */}
                        <div className="p-[4px] border-b-[1px] h-[44px] border-gray-500">
                          <div className="flex items-center gap-1.5 pl-[10px] pr-[12px] py-2">
                            <img src={SearchSm} />
                            <input
                              type="text"
                              placeholder="Search team"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="bg-transparent text-white text-[14px] focus:ring-0 focus:outline-none font-medium leading-[20px] fontoutline-none w-full placeholder:text-gray-800"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Dropdown Items */}
                        <div className="overflow-y-auto no-scrollbar p-1 pt-2 flex flex-col gap-[4px] max-h-[128px]">
                          {loadingTeams ? (
                            <CircleLoader />
                          ) : (
                            teams
                              .filter(
                                (team) =>
                                  team.name
                                    .toLowerCase()
                                    .includes(searchQuery.toLowerCase()) ||
                                  team.key
                                    .toLowerCase()
                                    .includes(searchQuery.toLowerCase())
                              )
                              .map((team) => (
                                <button
                                  key={team.id}
                                  type="button"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      teamId: team.id,
                                      assigneeId: "",
                                      parentIssueId: "",
                                      stateId: "",
                                      projectId: "",
                                    });
                                    setOpenDropdown(null);
                                    setSearchQuery("");
                                  }}
                                  className={`w-full pl-[10px] hover:bg-gray-500 pr-[12px] py-2 text-left rounded-[12px] text-white text-[14px] leading-[20px] transition-colors flex items-center justify-between ${
                                    formData.teamId === team.id
                                      ? "bg-gray-500"
                                      : "bg-transparent"
                                  }`}
                                >
                                  <span>{team.name}</span>
                                  {formData.teamId === team.id && (
                                    <img src={Checkmark} />
                                  )}
                                </button>
                              ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Project Selection */}
                <div className="relative shrink-0" ref={projectDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!formData.teamId) {
                        toast.error("Please select a team first");
                        return;
                      }
                      setOpenDropdown(
                        openDropdown === "project" ? null : "project"
                      );
                      setSearchQuery("");
                    }}
                    disabled={!formData.teamId}
                    className="grid grid-cols-2 w-full h-[32px] items-center text-[14px] leading-[20px] px-3 gap-1.5 rounded-[12px] hover:bg-white/[0.08] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <p className="text-left">Project</p>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white truncate">
                        {formData.projectId
                          ? projects.find((p) => p.id === formData.projectId)
                              ?.name
                          : "None"}
                      </span>
                      <img src={ChevronDownSm} />
                    </div>
                  </button>

                  <AnimatePresence>
                    {openDropdown === "project" && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`absolute max-h-[172px] w-[50%] right-0 bg-gray-400 rounded-[16px] overflow-hidden z-50 shadow-2xl ${
                          dropdownPosition.project === "top"
                            ? "bottom-full mb-2"
                            : "top-full mt-2"
                        }`}
                      >
                        {/* Search Input */}
                        <div className="p-[4px] border-b-[1px] h-[44px] border-gray-500">
                          <div className="flex items-center gap-1.5 pl-[10px] pr-[12px] py-2">
                            <img src={SearchSm} />
                            <input
                              type="text"
                              placeholder="Search project"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="bg-transparent text-white text-[14px] focus:ring-0 focus:outline-none font-medium leading-[20px] fontoutline-none w-full placeholder:text-gray-800"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Dropdown Items */}
                        <div className="overflow-y-auto no-scrollbar p-1 pt-2 flex flex-col gap-[4px] max-h-[128px]">
                          {loadingProjects ? (
                            <CircleLoader />
                          ) : (
                            projects
                              .filter((project) =>
                                project.name
                                  .toLowerCase()
                                  .includes(searchQuery.toLowerCase())
                              )
                              .map((project) => (
                                <button
                                  key={project.id}
                                  type="button"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      projectId: project.id,
                                    });
                                    setOpenDropdown(null);
                                    setSearchQuery("");
                                  }}
                                  className={`w-full pl-[10px] hover:bg-gray-500 pr-[12px] py-2 text-left rounded-[12px] text-white text-[14px] leading-[20px] transition-colors flex items-center justify-between ${
                                    formData.projectId === project.id
                                      ? "bg-gray-500"
                                      : "bg-transparent"
                                  }`}
                                >
                                  <span>{project.name}</span>
                                  {formData.projectId === project.id && (
                                    <img src={Checkmark} />
                                  )}
                                </button>
                              ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* State Selection */}
                <div className="relative shrink-0" ref={stateDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!formData.teamId) {
                        toast.error("Please select a team first");
                        return;
                      }
                      setOpenDropdown(
                        openDropdown === "state" ? null : "state"
                      );
                      setSearchQuery("");
                    }}
                    disabled={!formData.teamId}
                    className="grid grid-cols-2 w-full h-[32px] items-center text-[14px] leading-[20px] px-3 gap-1.5 rounded-[12px] hover:bg-white/[0.08] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <p className="text-left">State</p>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white truncate">
                        {formData.stateId
                          ? states.find((s) => s.id === formData.stateId)?.name
                          : "Default"}
                      </span>
                      <img src={ChevronDownSm} />
                    </div>
                  </button>

                  <AnimatePresence>
                    {openDropdown === "state" && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`absolute max-h-[172px] w-[50%] right-0 bg-gray-400 rounded-[16px] overflow-hidden z-50 shadow-2xl ${
                          dropdownPosition.state === "top"
                            ? "bottom-full mb-2"
                            : "top-full mt-2"
                        }`}
                      >
                        {/* Search Input */}
                        <div className="p-[4px] border-b-[1px] h-[44px] border-gray-500">
                          <div className="flex items-center gap-1.5 pl-[10px] pr-[12px] py-2">
                            <img src={SearchSm} />
                            <input
                              type="text"
                              placeholder="Search state"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="bg-transparent text-white text-[14px] focus:ring-0 focus:outline-none font-medium leading-[20px] fontoutline-none w-full placeholder:text-gray-800"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Dropdown Items */}
                        <div className="overflow-y-auto no-scrollbar p-1 pt-2 flex flex-col gap-[4px] max-h-[128px]">
                          {loadingStates ? (
                            <CircleLoader />
                          ) : (
                            states
                              .filter((state) =>
                                state.name
                                  .toLowerCase()
                                  .includes(searchQuery.toLowerCase())
                              )
                              .map((state) => (
                                <button
                                  key={state.id}
                                  type="button"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      stateId: state.id,
                                    });
                                    setOpenDropdown(null);
                                    setSearchQuery("");
                                  }}
                                  className={`w-full pl-[10px] hover:bg-gray-500 pr-[12px] py-2 text-left rounded-[12px] text-white text-[14px] leading-[20px] transition-colors flex items-center justify-between ${
                                    formData.stateId === state.id
                                      ? "bg-gray-500"
                                      : "bg-transparent"
                                  }`}
                                >
                                  <span>{state.name}</span>
                                  {formData.stateId === state.id && (
                                    <img src={Checkmark} />
                                  )}
                                </button>
                              ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Parent Issue Selection */}
                <div className="relative shrink-0" ref={parentIssueDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!formData.teamId) {
                        toast.error("Please select a team first");
                        return;
                      }
                      setOpenDropdown(
                        openDropdown === "parentIssue" ? null : "parentIssue"
                      );
                      setSearchQuery("");
                    }}
                    disabled={!formData.teamId}
                    className="grid grid-cols-2 w-full h-[32px] items-center text-[14px] leading-[20px] px-3 gap-1.5 rounded-[12px] hover:bg-white/[0.08] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <p className="text-left">Parent Issue</p>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white truncate">
                        {formData.parentIssueId
                          ? parentIssues.find(
                              (i) => i.id === formData.parentIssueId
                            )?.identifier
                          : "None"}
                      </span>
                      <img src={ChevronDownSm} />
                    </div>
                  </button>

                  <AnimatePresence>
                    {openDropdown === "parentIssue" && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`absolute max-h-[172px] w-[50%] right-0 bg-gray-400 rounded-[16px] overflow-hidden z-50 shadow-2xl ${
                          dropdownPosition.parentIssue === "top"
                            ? "bottom-full mb-2"
                            : "top-full mt-2"
                        }`}
                      >
                        {/* Search Input */}
                        <div className="p-[4px] border-b-[1px] h-[44px] border-gray-500">
                          <div className="flex items-center gap-1.5 pl-[10px] pr-[12px] py-2">
                            <img src={SearchSm} />
                            <input
                              type="text"
                              placeholder="Search parent issue"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="bg-transparent text-white text-[14px] focus:ring-0 focus:outline-none font-medium leading-[20px] fontoutline-none w-full placeholder:text-gray-800"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Dropdown Items */}
                        <div className="overflow-y-auto no-scrollbar p-1 pt-2 flex flex-col gap-[4px] max-h-[128px]">
                          {loadingParentIssues ? (
                            <CircleLoader />
                          ) : (
                            parentIssues
                              .filter(
                                (issue) =>
                                  issue.identifier
                                    .toLowerCase()
                                    .includes(searchQuery.toLowerCase()) ||
                                  issue.title
                                    .toLowerCase()
                                    .includes(searchQuery.toLowerCase())
                              )
                              .map((issue) => (
                                <button
                                  key={issue.id}
                                  type="button"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      parentIssueId: issue.id,
                                    });
                                    setOpenDropdown(null);
                                    setSearchQuery("");
                                  }}
                                  className={`w-full pl-[10px] hover:bg-white/[0.08] pr-[12px] py-2 text-left rounded-[12px] text-white text-[14px] leading-[20px] transition-colors flex items-center justify-between ${
                                    formData.parentIssueId === issue.id
                                      ? "bg-gray-500"
                                      : "bg-transparent"
                                  }`}
                                >
                                  <span className="truncate">
                                    {issue.identifier} - {issue.title}
                                  </span>
                                  {formData.parentIssueId === issue.id && (
                                    <img src={Checkmark} />
                                  )}
                                </button>
                              ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Priority Selection */}
                <div className="relative shrink-0" ref={priorityDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpenDropdown(
                        openDropdown === "priority" ? null : "priority"
                      );
                      setSearchQuery("");
                    }}
                    className="grid grid-cols-2 w-full h-[32px] items-center text-[14px] leading-[20px] px-3 gap-1.5 rounded-[12px] hover:bg-white/[0.08] transition-colors"
                  >
                    <p className="text-left">Priority</p>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white truncate">
                        {formData.priority
                          ? priorities.find((p) => p.id === formData.priority)
                              ?.name
                          : "No priority"}
                      </span>
                      <img src={ChevronDownSm} />
                    </div>
                  </button>

                  <AnimatePresence>
                    {openDropdown === "priority" && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`absolute max-h-[172px] w-[50%] right-0 bg-gray-400 rounded-[16px] overflow-hidden z-50 shadow-2xl ${
                          dropdownPosition.priority === "top"
                            ? "bottom-full mb-2"
                            : "top-full mt-2"
                        }`}
                      >
                        {/* Search Input */}
                        <div className="p-[4px] border-b-[1px] h-[44px] border-gray-500">
                          <div className="flex items-center gap-1.5 pl-[10px] pr-[12px] py-2">
                            <img src={SearchSm} />
                            <input
                              type="text"
                              placeholder="Search priority"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="bg-transparent text-white text-[14px] focus:ring-0 focus:outline-none font-medium leading-[20px] fontoutline-none w-full placeholder:text-gray-800"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Dropdown Items */}
                        <div className="overflow-y-auto no-scrollbar p-1 pt-2 flex flex-col gap-[4px] max-h-[128px]">
                          {priorities
                            .filter((priority) =>
                              priority.name
                                .toLowerCase()
                                .includes(searchQuery.toLowerCase())
                            )
                            .map((priority) => (
                              <button
                                key={priority.id}
                                type="button"
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    priority: priority.id,
                                  });
                                  setOpenDropdown(null);
                                  setSearchQuery("");
                                }}
                                className={`w-full pl-[10px] hover:bg-white/[0.08] pr-[12px] py-2 text-left rounded-[12px] text-white text-[14px] leading-[20px] transition-colors flex items-center justify-between ${
                                  formData.priority === priority.id
                                    ? "bg-gray-500"
                                    : "bg-transparent"
                                }`}
                              >
                                <span>{priority.name}</span>
                                {formData.priority === priority.id && (
                                  <img src={Checkmark} />
                                )}
                              </button>
                            ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Assignee Selection */}
                <div className="relative shrink-0" ref={assigneeDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!formData.teamId) {
                        toast.error("Please select a team first");
                        return;
                      }
                      setOpenDropdown(
                        openDropdown === "assignee" ? null : "assignee"
                      );
                      setSearchQuery("");
                    }}
                    disabled={!formData.teamId}
                    className="grid grid-cols-2 w-full h-[32px] items-center text-[14px] leading-[20px] px-3 gap-1.5 rounded-[12px] hover:bg-white/[0.08] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <p className="text-left">Assignee</p>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white truncate">
                        {formData.assigneeId
                          ? users.find((u) => u.id === formData.assigneeId)
                              ?.displayName
                          : "Unassigned"}
                      </span>
                      <img src={ChevronDownSm} />
                    </div>
                  </button>

                  <AnimatePresence>
                    {openDropdown === "assignee" && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`absolute max-h-[172px] w-[50%] right-0 bg-gray-400 rounded-[16px] overflow-hidden z-50 shadow-2xl ${
                          dropdownPosition.assignee === "top"
                            ? "bottom-full mb-2"
                            : "top-full mt-2"
                        }`}
                      >
                        {/* Search Input */}
                        <div className="p-[4px] border-b-[1px] h-[44px] border-gray-500">
                          <div className="flex items-center gap-1.5 pl-[10px] pr-[12px] py-2">
                            <img src={SearchSm} />
                            <input
                              type="text"
                              placeholder="Search assignee"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="bg-transparent text-white text-[14px] focus:ring-0 focus:outline-none font-medium leading-[20px] fontoutline-none w-full placeholder:text-gray-800"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Dropdown Items */}
                        <div className="overflow-y-auto no-scrollbar p-1 pt-2 flex flex-col gap-[4px] max-h-[128px]">
                          {loadingUsers ? (
                            <CircleLoader />
                          ) : (
                            users
                              .filter((user) =>
                                user.displayName
                                  .toLowerCase()
                                  .includes(searchQuery.toLowerCase())
                              )
                              .map((user) => (
                                <button
                                  key={user.id}
                                  type="button"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      assigneeId: user.id,
                                    });
                                    setOpenDropdown(null);
                                    setSearchQuery("");
                                  }}
                                  className={`w-full pl-[10px] hover:bg-gray-500 pr-[12px] py-2 text-left rounded-[12px] text-white text-[14px] leading-[20px] transition-colors flex items-center justify-between ${
                                    formData.assigneeId === user.id
                                      ? "bg-gray-500"
                                      : "bg-transparent"
                                  }`}
                                >
                                  <span>{user.displayName}</span>
                                  {formData.assigneeId === user.id && (
                                    <img src={Checkmark} />
                                  )}
                                </button>
                              ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={creating || !formData.teamId || !formData.title}
                className=" bg-blue hover:bg-blue/[0.8] rounded-[14px] !h-[44px] shrink-0 text-[14px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Push issue to Linear
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LinearIssueModal;
